import React, { useRef, useState, useEffect } from "react";
import Icon from "./Icon";
import LiveKitVideo from "./LiveKitVideo";
import { getAvatarColors } from "../utils/avatar";
import "./MiniFloatingControls.css";

export default function MiniFloatingControls({
  visible,
  localTrackRef,
  micOn,
  camOn,
  isSharing,
  onToggleMic,
  onToggleCam,
  onToggleScreenShare,
  onLeave,
  canPublishAudio,
  canPublishVideo,
  canPublishScreen,
  participantCount = 0,
  myName = "You", // Add this prop
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [initialPos, setInitialPos] = useState({ x: 0, y: 0 });

  const dragRef = useRef(null);

  // Initialize position to bottom-right on mount
  useEffect(() => {
    if (!visible) return;

    const updateInitialPosition = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Default bottom-right position (accounting for element size)
      const defaultX = windowWidth - 280; // 280 = approx width + padding
      const defaultY = windowHeight - 320; // 320 = approx height + padding

      setPosition({ x: defaultX, y: defaultY });
    };

    updateInitialPosition();

    window.addEventListener("resize", updateInitialPosition);
    return () => window.removeEventListener("resize", updateInitialPosition);
  }, [visible]);

  const handleMouseDown = (e) => {
    if (e.target.closest(".jt-miniFloat-btn")) return; // Don't drag when clicking buttons

    setIsDragging(true);
    setInitialPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - initialPos.x;
    const newY = e.clientY - initialPos.y;

    // Constrain to viewport
    const maxX = window.innerWidth - 250;
    const maxY = window.innerHeight - 300;

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, initialPos, position]);

  // Touch support for mobile
  const handleTouchStart = (e) => {
    if (e.target.closest(".jt-miniFloat-btn")) return;

    const touch = e.touches[0];
    setIsDragging(true);
    setInitialPos({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });

    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newX = touch.clientX - initialPos.x;
    const newY = touch.clientY - initialPos.y;

    const maxX = window.innerWidth - 250;
    const maxY = window.innerHeight - 300;

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, initialPos, position]);

  if (!visible) return null;

  const [c1, c2] = getAvatarColors(myName);

  return (
    <div
      ref={dragRef}
      className={`jt-miniFloat ${isDragging ? "dragging" : ""}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="jt-miniFloat-inner">
        {/* Drag handle indicator */}
        <div className="jt-miniFloat-dragHandle">
          <div className="jt-miniFloat-dragBar" />
        </div>

        {/* Self-view video */}
        <div className="jt-miniFloat-video">
          {localTrackRef && camOn ? (
            <LiveKitVideo
              trackRef={localTrackRef}
              muted
              mirror
              objectFit="cover"
              className="jt-miniFloat-videoEl"
            />
          ) : (
            <div
              className="jt-miniFloat-avatar"
              style={{
                background: `linear-gradient(135deg, ${c1}, ${c2})`,
              }}
            >
              <span className="jt-miniFloat-avatarText">
                {(myName?.[0] || "Y").toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Participant count */}
        {participantCount > 0 && (
          <div className="jt-miniFloat-count">
            <Icon name="people" />
            <span>
              {participantCount} participant{participantCount !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Controls */}
        <div className="jt-miniFloat-controls">
          {canPublishAudio && (
            <button
              className={`jt-miniFloat-btn ${micOn ? "active" : "danger"}`}
              onClick={onToggleMic}
              title={micOn ? "Mute" : "Unmute"}
            >
              {micOn ? <Icon name="mic" /> : <Icon name="micOff" />}
            </button>
          )}

          {canPublishVideo && (
            <button
              className={`jt-miniFloat-btn ${camOn ? "active" : "danger"}`}
              onClick={onToggleCam}
              title={camOn ? "Camera off" : "Camera on"}
            >
              {camOn ? <Icon name="cam" /> : <Icon name="camOff" />}
            </button>
          )}

          {canPublishScreen && (
            <button
              className={`jt-miniFloat-btn ${isSharing ? "broadcast" : "inactive"}`}
              onClick={onToggleScreenShare}
              title={isSharing ? "Stop sharing" : "Share screen"}
            >
              {isSharing ? (
                <Icon name="screenShareOff" />
              ) : (
                <Icon name="screen" />
              )}
            </button>
          )}

          <button
            className="jt-miniFloat-btn danger"
            onClick={onLeave}
            title="Leave"
          >
            <Icon name="leave" />
          </button>
        </div>
      </div>

      {/* Indicator */}
      <div className="jt-miniFloat-indicator">
        {isSharing ? "You're presenting to the meeting" : "In meeting"}
      </div>
    </div>
  );
}
